import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Skeleton,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { api } from '../services/api';

interface Summary {
  month: string;
  totalIncome: number;
  totalExpense: number;
  netAmount: number;
  byCategory: Array<{
    categoryId: string;
    categoryName: string;
    totalAmountEur: number;
    transactionCount: number;
  }>;
}

export default function DashboardPage() {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);

  const currentMonth = new Date().toISOString().substring(0, 7);

  useEffect(() => {
    api
      .get<Summary>(`/api/reports/summary?month=${currentMonth}`)
      .then(setSummary)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [currentMonth]);

  const formatEur = (amount: number) =>
    new Intl.NumberFormat('fi-FI', { style: 'currency', currency: 'EUR' }).format(amount);

  if (loading) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom>Yhteenveto</Typography>
        <Grid container spacing={2}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} sm={4} key={i}>
              <Skeleton variant="rounded" height={120} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom fontWeight={600}>
        {new Date().toLocaleDateString('fi-FI', { month: 'long', year: 'numeric' })}
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <TrendingUpIcon />
                <Typography variant="body2">Tulot</Typography>
              </Box>
              <Typography variant="h5" fontWeight={700}>
                {formatEur(summary?.totalIncome || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ bgcolor: 'error.light', color: 'error.contrastText' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <TrendingDownIcon />
                <Typography variant="body2">Menot</Typography>
              </Box>
              <Typography variant="h5" fontWeight={700}>
                {formatEur(Math.abs(summary?.totalExpense || 0))}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <AccountBalanceWalletIcon />
                <Typography variant="body2">Netto</Typography>
              </Box>
              <Typography variant="h5" fontWeight={700}>
                {formatEur(summary?.netAmount || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom>
        Suurimmat kategoriat
      </Typography>
      <Grid container spacing={2}>
        {(summary?.byCategory || [])
          .sort((a, b) => Math.abs(b.totalAmountEur) - Math.abs(a.totalAmountEur))
          .slice(0, 8)
          .map((cat) => (
            <Grid item xs={12} sm={6} md={3} key={cat.categoryId}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {cat.categoryName}
                  </Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {formatEur(Math.abs(cat.totalAmountEur))}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {cat.transactionCount} tapahtumaa
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
      </Grid>
    </Box>
  );
}
